"use client"

import { FormEvent, useEffect, useState } from "react"
import Link from "next/link"
import { ArrowRight, Eye, EyeOff, LoaderCircle } from "lucide-react"
import { SignalsLogo } from "@/components/signals-logo"
import { useRegistrationStatus } from "@/lib/use-registration-status"

export default function LoginPage() {
  const registration = useRegistrationStatus()

  const [from, setFrom] = useState("/")
  const [showPass, setShowPass] = useState(false)
  const [loading, setLoading] = useState(false)
  const [demoLoading, setDemoLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (typeof window === "undefined") return
    const params = new URLSearchParams(window.location.search)
    setFrom(params.get("next") ?? params.get("from") ?? "/")
  }, [])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData(e.currentTarget)
    const email = String(formData.get("email") ?? "").trim()
    const password = String(formData.get("password") ?? "")

    if (!email || !password) {
      setError("Please enter your email and password.")
      return
    }

    setLoading(true)
    setError(null)
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email, password }),
      })
      const data = await res.json()
      if (!res.ok) {
        const validationError = data.errors ? Object.values(data.errors).flat()[0] : null
        setError(typeof validationError === "string" ? validationError : data.error ?? data.message ?? "Login failed.")
        setLoading(false)
        return
      }
      const destination = data.user?.role === "platform_admin" && from === "/"
        ? "/admin"
        : from
      window.location.assign(destination)
    } catch {
      setError("Something went wrong — please try again.")
      setLoading(false)
    }
  }

  const openDemo = async () => {
    setDemoLoading(true)
    setError(null)
    try {
      const res = await fetch("/api/auth/demo", {
        method: "POST",
        headers: { Accept: "application/json" },
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(data.error ?? "The public demo is temporarily unavailable.")
        setDemoLoading(false)
        return
      }
      window.location.assign("/")
    } catch {
      setError("The public demo is temporarily unavailable.")
      setDemoLoading(false)
    }
  }

  const pageLoading = loading || demoLoading

  return (
    <div
      className="min-h-screen overflow-hidden bg-[#f3f6ff] px-4 py-5 text-slate-950 sm:px-6 sm:py-10 lg:px-8"
      aria-busy={pageLoading}
    >
      {pageLoading && (
        <div
          className="fixed inset-0 z-[100] flex cursor-wait items-center justify-center bg-[#f3f6ff]/90 px-6 backdrop-blur-sm"
          role="status"
          aria-live="polite"
          aria-label={demoLoading ? "Opening the Signals demo" : "Signing in to Signals"}
        >
          <div className="flex max-w-sm flex-col items-center rounded-3xl border border-slate-200/80 bg-white px-8 py-7 text-center shadow-2xl shadow-blue-950/10">
            <LoaderCircle className="size-9 animate-spin text-[#314db7]" aria-hidden="true" />
            <p className="mt-4 text-lg font-semibold tracking-[-0.035em] text-slate-950">
              {demoLoading ? "Opening the live demo…" : "Signing you in…"}
            </p>
            <p className="mt-1.5 text-sm leading-6 text-slate-500">
              {demoLoading ? "Loading the Northstar Commerce dashboard" : "Loading your Signals dashboard"}
            </p>
          </div>
        </div>
      )}
      <main className="mx-auto flex min-h-[calc(100vh-2.5rem)] w-full max-w-sm flex-col justify-center sm:min-h-[calc(100vh-5rem)] sm:max-w-md">
        <header className="mb-6 text-center sm:mb-8">
          <SignalsLogo className="mx-auto mb-3 size-14 shadow-2xl shadow-blue-900/15 sm:mb-4 sm:size-16" />
          <h1 className="text-[2.1rem] font-semibold leading-tight tracking-[-0.055em] text-slate-950 sm:text-4xl">
            Signals
          </h1>
          <p className="mt-1.5 text-[0.95rem] font-medium tracking-[-0.025em] text-slate-500 sm:mt-2 sm:text-lg">
            Support Operations Diagnostic
          </p>
        </header>

        <section className="rounded-[1.4rem] border border-slate-200/80 bg-white/90 px-4 py-5 shadow-xl shadow-blue-950/[0.04] sm:rounded-[1.75rem] sm:px-8 sm:py-8">
          <div className="mb-5 sm:mb-6">
            <h2 className="text-xl font-semibold leading-tight tracking-[-0.045em] text-slate-950 sm:text-2xl">
              Sign in to your account
            </h2>
            <p className="mt-2 text-[0.95rem] leading-6 tracking-[-0.025em] text-slate-500 sm:mt-2 sm:text-base sm:leading-6">
              Enter your credentials to access the platform
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4 sm:gap-5">
            <label className="space-y-1.5 sm:space-y-2">
              <span className="block text-[0.95rem] font-medium tracking-[-0.03em] text-slate-950 sm:text-base">Email address</span>
              <input
                type="email"
                name="email"
                placeholder="you@company.com"
                autoComplete="email"
                autoFocus
                className="h-12 w-full rounded-xl border border-slate-200 bg-[#f3f6fc] px-4 text-[0.95rem] text-slate-950 shadow-inner shadow-slate-900/[0.03] transition-colors placeholder:text-slate-400 focus:border-[#314db7]/50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#314db7]/10 sm:h-14 sm:rounded-2xl sm:px-5 sm:text-base"
              />
            </label>

            <label className="space-y-1.5 sm:space-y-2">
              <span className="block text-[0.95rem] font-medium tracking-[-0.03em] text-slate-950 sm:text-base">Password</span>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  name="password"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  className="h-12 w-full rounded-xl border border-slate-200 bg-[#f3f6fc] px-4 pr-12 text-[0.95rem] text-slate-950 shadow-inner shadow-slate-900/[0.03] transition-colors placeholder:text-slate-400 focus:border-[#314db7]/50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#314db7]/10 sm:h-14 sm:rounded-2xl sm:px-5 sm:pr-14 sm:text-base"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-xl p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 sm:right-4"
                  aria-label={showPass ? "Hide password" : "Show password"}
                >
                  {showPass ? <EyeOff className="size-4 sm:size-5" /> : <Eye className="size-4 sm:size-5" />}
                </button>
              </div>
            </label>

            {error && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-1 flex h-12 w-full items-center justify-center gap-2.5 rounded-xl bg-[#314db7] px-5 text-base font-semibold tracking-[-0.02em] text-white shadow-xl shadow-blue-900/15 transition-all hover:-translate-y-0.5 hover:bg-[#2943a7] disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60 sm:h-14 sm:rounded-2xl"
            >
              {loading ? "Signing in…" : "Sign in"}
              {!loading && <ArrowRight className="size-5" />}
            </button>
          </form>

          {registration.demoEnabled && !registration.loading && (
            <>
              <div className="my-5 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400 sm:my-6">
                <span className="h-px flex-1 bg-slate-200" />
                or
                <span className="h-px flex-1 bg-slate-200" />
              </div>
              <button
                type="button"
                onClick={openDemo}
                disabled={demoLoading || loading}
                className="flex h-12 w-full items-center justify-center gap-2.5 rounded-xl border border-[#314db7]/20 bg-[#eef2ff] px-5 text-base font-semibold tracking-[-0.02em] text-[#314db7] transition-all hover:-translate-y-0.5 hover:border-[#314db7]/35 hover:bg-[#e6ebff] disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60 sm:h-14 sm:rounded-2xl"
              >
                {demoLoading ? "Opening demo…" : "Explore the live demo"}
                {!demoLoading && <ArrowRight className="size-5" />}
              </button>
              <p className="mt-3 text-center text-xs leading-5 text-slate-500">
                Explore six months of synthetic support operations data. The demo is read-only.
              </p>
            </>
          )}

          <div className="mt-5 flex items-center justify-between gap-4 text-sm tracking-[-0.025em] sm:mt-6 sm:text-base">
            <Link href="/forgot-password" className="font-normal text-slate-500 transition-colors hover:text-slate-950">
              Forgot password?
            </Link>
            {registration.enabled && !registration.loading && (
              <Link href="/register" className="font-semibold text-[#314db7] underline decoration-[#314db7]/70 decoration-2 underline-offset-4 hover:text-[#2943a7]">
                Sign up free
              </Link>
            )}
          </div>
        </section>

        <p className="mt-5 text-center text-xs text-slate-500 sm:mt-6 sm:text-sm">
          Signals &copy; {new Date().getFullYear()} &middot; <a href="mailto:support@signals-app.com" className="font-medium text-slate-500 hover:text-slate-950">Contact support</a>
        </p>
      </main>
    </div>
  )
}
